import { ClassValue } from 'clsx';
import { KeyboardEvent } from 'react';
import { AnchoredView, AnchoredViewProps } from '@shared/components/AnchoredSheet';
import { ButtonBarContext } from '@shared/components/Button/ButtonBarContext';
import { HeroPortal } from '@shared/components/HeroPortal';
import { View } from '@shared/components/View';
import { useMenuContext } from './MenuContext';
import styles from './Menu.module.css';

/**
 * Menu.Content
 *
 * The floating dropdown container. Renders anchored to the nearest
 * `Menu.Trigger` or `Menu.Button` and traps focus while open. Returns null
 * when the menu is closed, so children are unmounted between opens.
 *
 * Pass `unpadded` to remove the default vertical padding (useful when the
 * content manages its own spacing). Pass `showScrollIndicators` to display
 * fade masks at the top and bottom when the list overflows.
 */

export interface MenuContentProps extends Omit<AnchoredViewProps, 'anchorName' | 'onClose'> {
  className?: ClassValue;
}

export function MenuContent({ children, className, ...rest }: MenuContentProps) {
  const { anchorName, isOpen, close, align, lightDismiss } = useMenuContext();

  if (!isOpen) {
    return null;
  }

  return (
    <AnchoredView
      anchorName={anchorName}
      onClose={close}
      className={[
        styles.menu,
        align === 'left' && styles.alignLeft,
        align === 'center' && styles.alignCenter,
        className,
      ]}
      lightDismiss={lightDismiss}
      role="menu"
      trapFocus={isOpen}
      {...rest}
    >
      <HeroPortal topLayer>
        <ButtonBarContext.Provider value={null}>
          <View className={[styles.menuContent]} data-content onKeyDown={stopKeyPropagation}>
            {children}
          </View>
        </ButtonBarContext.Provider>
      </HeroPortal>
    </AnchoredView>
  );
}

/**
 * Keystrokes inside a menu belong to the menu, don't let them bubble up to the
 * parent. This prevents enter/return from triggering any parent button press
 * events. It doesn't affect the menu's focus trap or tab/escape handling.
 */
function stopKeyPropagation(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    return;
  }
  event.stopPropagation();
}
